import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AnimatedSection, StaggerChildren, StaggerItem } from '../components/AnimatedSection';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_elite-clarity/artifacts/fuokd9n3_paragon.jpg";

export const Home = () => {
  return (
    <div data-testid="home-page" className="bg-[#050505]">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Focus before performance"
            className="w-full h-full object-cover opacity-40" />

          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/40 to-[#050505]" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}>

            <p className="caption-text !text-base mb-8">The Quiet Advantage</p>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="heading-large mb-8">

            Make Performance<br />Inevitable
          </motion.h1>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="max-w-xl mx-auto">

            <p className="body-large mb-4">
              Elite performers operate in environments of extreme pressure.
            </p>
            <p className="body-text mb-4">
              Noise. Expectation. Constant advice.
            </p>
            <p className="body-text mb-12">
              Paragon works somewhere different. <span className="text-white">At the source of your experience.</span><br />
              Where performance actually comes from.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center">

            <Link to="/apply" data-testid="hero-apply-btn" className="btn-primary">
              Contact Paragon
            </Link>
            <Link to="/philosophy" data-testid="hero-philosophy-btn" className="btn-secondary">
              Our Philosophy
            </Link>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2">

          <div className="w-px h-16 bg-gradient-to-b from-transparent via-white/30 to-transparent" />
        </motion.div>
      </section>

      {/* The Problem Section */}
      <section data-testid="problem-section" className="section-padding">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <AnimatedSection>
              <p className="caption-text mb-6">The Problem</p>
              <h2 className="heading-medium">
                Elite performers are surrounded by advice.
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="space-y-6">
                <p className="body-large">
                  Psychologists. Coaches. Consultants.<br />
                  Systems and modalities.
                </p>
                <p className="body-text">
                  All offering strategies for what to do.
                </p>
                <div className="divider my-8" />
                <p className="body-text">
                  Yet the greatest performers know something deeper.
                </p>
                <p className="text-white text-lg font-['Playfair_Display'] italic">
                  Peak performance is not created through technique, it emerges naturally when the mind is clear.<br />
                  
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* The Paragon Approach Section */}
      <section data-testid="approach-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-[900px] mx-auto text-center">
          <AnimatedSection>
            <p className="caption-text mb-6">The Paragon Approach</p>
            <h2 className="heading-medium mb-12">
              We begin somewhere else.
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="body-large mb-8">
              Paragon does not focus on strategies, tools or techniques.
            </p>
            <p className="body-text mb-8">We operate from a deeper understanding of where experience actually comes from.

            </p>
            <p className="body-text mb-12">
              When this becomes clear, something remarkable happens.
            </p>
          </AnimatedSection>

          <StaggerChildren className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <StaggerItem>
              <div className="glass-card text-center">
                <p className="text-white font-['Playfair_Display'] text-xl mb-2">Pressure</p>
                <p className="text-[#666] text-sm">looks different</p>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="glass-card text-center">
                <p className="text-white font-['Playfair_Display'] text-xl mb-2">Clarity</p>
                <p className="text-[#666] text-sm">returns</p>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="glass-card text-center">
                <p className="text-white font-['Playfair_Display'] text-xl mb-2">Confidence</p>
                <p className="text-[#666] text-sm">stops fluctuating</p>
              </div>
            </StaggerItem>
          </StaggerChildren>

          <AnimatedSection delay={0.6}>
            <p className="text-white text-xl font-['Playfair_Display'] italic">
              From this space, performance becomes inevitable.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section data-testid="who-section" className="section-padding">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <AnimatedSection>
              <p className="caption-text mb-6">Who We Work With</p>
              <h2 className="heading-medium mb-8">
                A small number of exceptional individuals.
              </h2>
              <p className="body-large mb-6">
                Paragon works with a small number of individuals each year.
              </p>
              <p className="body-text mb-6">
                Primarily elite athletes operating in high-pressure environments.
              </p>
              <p className="body-text">We also support a limited number of leaders, teams and organisations who want to perform at the highest level.

              </p>
              <div className="divider my-8" />
              <p className="text-white/80 text-sm italic">Every Paragon relationship begins with a private conversation.

              </p>
            </AnimatedSection>

            <AnimatedSection delay={0.3} direction="left">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_elite-clarity/artifacts/cxiwujt5_WhatsApp%20Image%202026-03-19%20at%2018.38.23.jpeg"
                  alt="High performance environment"
                  className="w-full aspect-[4/3] object-cover grayscale" />

                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Invitation Section */}
      <section data-testid="invitation-section" className="section-padding relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1678250991250-beb4b9cb297b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjb25jcmV0ZSUyMG1pbmltYWxpc3QlMjBhcmNoaXRlY3R1cmUlMjBkYXJrfGVufDB8fHx8MTc3MzMyNzkxNnww&ixlib=rb-4.1.0&q=85"
            alt="Architecture"
            className="w-full h-full object-cover opacity-20" />

          <div className="absolute inset-0 bg-[#050505]/80" />
        </div>

        <div className="relative z-10 max-w-[800px] mx-auto text-center">
          <AnimatedSection>
            <p className="caption-text mb-6">Invitation</p>
            <h2 className="heading-medium mb-8">
              Begin the Conversation
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="body-large mb-6">
              Paragon operates primarily through invitation and referral.
            </p>
            <p className="body-text mb-12">
              If this work resonates with you, we invite you to get in touch.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <Link to="/apply" data-testid="invitation-apply-btn" className="btn-primary">
              Contact Paragon
            </Link>
          </AnimatedSection>
        </div>
      </section>

      {/* Contribution Section */}
      <section data-testid="contribution-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-[800px] mx-auto text-center">
          <AnimatedSection>
            <p className="caption-text mb-6">Contribution</p>
            <h2 className="heading-medium mb-8">
              Paragon believes deeply in contribution
            </h2>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="body-large mb-6">
              Paragon partnerships operate at a premium level.
            </p>
            <p className="body-text mb-6">
              In select cases, we choose to make this work available where it is truly needed.
            </p>
            <p className="text-white/80 italic">
              What matters most is the conversation.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </div>);

};